import { computedFrom } from "aurelia-binding";
import * as Enumerable from "linq";
import * as moment from "moment";
import { CompSafetyScore } from "@rightship-tech/common-ui/components/comp-safety-score/comp-safety-score";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { VesselApi } from "../../../../../apis/vessel.api";
import { DueDiligenceClientModel, VetRunRiskLevelsEnum, VetStatusEnum } from "../../../../../models/due-diligence.models";

export abstract class BaseVetOverview {

    public item: DueDiligenceClientModel;
    
    public activate(model: any) {
        this.item = typeof (model) == "string" ? this.parse(<string>model) : model;
    }

    private parse(obj: string): DueDiligenceClientModel {
        return JSON.parse(obj.replace(new RegExp("&quot;", "g"), "\"")
            .replace(new RegExp("&gt;", "g"), ">")
            .replace(new RegExp("&lt;", "g"), "<"));
    }

    
    public isShowMoreSectionDetails: boolean;
    public isShowMoreSectionStatus: boolean;
    public compSafetyScore: CompSafetyScore;
    public decisionComments: string;

    constructor(protected vesselApi: VesselApi, protected configService: ConfigService) {
    }

    @computedFrom("item", "item.vetStatusId")
    get notProcessing() {
        return this.item && this.item.vetStatusId != VetStatusEnum.Processing;
    }

    @computedFrom("vetRiskScores")
    get vetRiskScoresEnumerable() { return Enumerable.from(this.vetRiskScores).where(x => x.name == 'All'); }

    @computedFrom("vetRiskScores")
    get countLowFail() { return this.vetRiskScoresEnumerable.sum(x => x.countLowFail); }

    @computedFrom("vetRiskScores")
    get countLowPending() { return this.vetRiskScoresEnumerable.sum(x => x.countLowPending); }

    @computedFrom("vetRiskScores")
    get countMediumFail() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumFail); }

    @computedFrom("vetRiskScores")
    get countMediumPending() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumPending); }

    @computedFrom("vetRiskScores")
    get countHighFail() { return this.vetRiskScoresEnumerable.sum(x => x.countHighFail); }

    @computedFrom("vetRiskScores")
    get countHighPending() { return this.vetRiskScoresEnumerable.sum(x => x.countHighPending); }

    @computedFrom("vetRiskScores")
    get countOraFail() { return this.vetRiskScoresEnumerable.sum(x => x.countOraFail); }

    @computedFrom("vetRiskScores")
    get countOraPending() { return this.vetRiskScoresEnumerable.sum(x => x.countOraPending); }

    @computedFrom("vetRiskScores")
    get countLowPass() { return this.vetRiskScoresEnumerable.sum(x => x.countLowPass); }

    @computedFrom("vetRiskScores")
    get countMediumPass() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumPass); }

    @computedFrom("vetRiskScores")
    get countHighPass() { return this.vetRiskScoresEnumerable.sum(x => x.countHighPass); }

    @computedFrom("vetRiskScores")
    get countOraPass() { return this.vetRiskScoresEnumerable.sum(x => x.countOraPass); }

    @computedFrom("vetRiskScores")
    get countAllLow() { return this.vetRiskScoresEnumerable.sum(x => x.countLowPending +  x.countLowFail + x.countLowPass); }

    @computedFrom("vetRiskScores")
    get countAllMedium() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumPending +x.countMediumFail + x.countMediumPass); }

    @computedFrom("vetRiskScores")
    get countAllHigh() { return this.vetRiskScoresEnumerable.sum(x => x.countHighPending +  x.countHighFail + x.countHighPass); }

    @computedFrom("vetRiskScores")
    get countAllOra() { return this.vetRiskScoresEnumerable.sum(x => x.countOraPending + x.countOraFail + x.countOraPass); }

    isCategoryCustomer(cat: string) {
        return Enumerable.from(this.item.vetTasks).where(x => x.category == cat).any(x => x.categoryIsCustomer);
    }

    @computedFrom("item")
    get vetRiskScores() {
        const vetRiskScores = this.item.vetRiskScores;
        if (vetRiskScores.filter(x => x.name == 'Critical').length == 0) {
            vetRiskScores.push({
                name: "Critical",
                riskLevelId: VetRunRiskLevelsEnum.Low,
                riskLevel: "Low",
                countHighFail: 0,
                countHighPending: 0,
                countHighPass: 0,
                countLowFail: 0,
                countLowPending: 0,
                countLowPass: 0,
                countMediumFail: 0,
                countMediumPending: 0,
                countMediumPass: 0,
                countOraFail: 0,
                countOraPending: 0,
                countOraPass: 0,
                sequence: 2,
                riskScore: null,
                title: "Critical Score",
                riskCategoryId: -1
            });
        }
        if (vetRiskScores.filter(x => x.name == 'Soft').length == 0) {
            vetRiskScores.push({
                name: "Soft",
                riskLevelId: VetRunRiskLevelsEnum.Low,
                riskLevel: "Low",
                countHighFail: 0,
                countHighPending: 0,
                countHighPass: 0,
                countLowFail: 0,
                countLowPending: 0,
                countLowPass: 0,
                countMediumFail: 0,
                countMediumPending: 0,
                countMediumPass: 0,
                countOraFail: 0,
                countOraPending: 0,
                countOraPass: 0,
                sequence: 3,
                riskScore: null,
                title: "Soft Score",
                riskCategoryId: -1
            });
        }

        const scores = this.item ? Enumerable.from(vetRiskScores).orderBy(x => x.sequence).toArray() : [];

        for (const score of scores) {
            score.riskScore = this.vesselApi.calculateRiskScore({
                countAllHigh:  score.countHighPending + score.countHighFail + score.countHighPass,
                countAllLow: score.countLowPending + score.countLowFail + score.countLowPass,
                countAllMedium:score.countMediumPending + score.countMediumFail + score.countMediumPass,
                countHighFail: score.countHighFail,
                countLowFail: score.countLowFail,
                countMediumFail: score.countMediumFail,
                countOraFail: score.countOraFail
            });
        }

        return scores;
    }

    @computedFrom("item", "item.vetTasks", "item.vetTasks.length", "task.vetSuperVetTaskOutcomeId")
    get pendingRules() {
        return this.item.vetTasks.filter(task => !task.vetSuperVetTaskOutcomeId || task.vetSuperVetTaskOutcomeId <= 0).length;
    }

    @computedFrom("item", "item.built")
    get built() {
        const date = moment(this.item.built);
        const yearAge = this.getAgeFromDate(date);
        return yearAge;
    }

    getAgeFromDate(date): string {
        return (Math.floor(moment().diff(date, 'year', true) * 10) / 10).toFixed(1) + "y/o" + " (" + moment(date).year() + ")"
    }

    showMoreSectionDetails() {
        this.isShowMoreSectionDetails = !this.isShowMoreSectionDetails;
    }

    showMoreSectionStatus() {
        this.isShowMoreSectionStatus = !this.isShowMoreSectionStatus;
    }
}